import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Sparkles, Trash2, Mail, Loader2, MailCheck, Pencil, Plus, Eye, EyeOff } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BlogEditor } from "@/components/admin/BlogEditor";
import api, { formatApiErrorDetail } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_TYPES = [
  { key: "welcome", label: "Benvinguda al visitant" },
  { key: "notification", label: "Notificació interna" },
];
const EMAIL_LANGS = [
  { key: "ca", label: "Català" },
  { key: "es", label: "Español" },
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
];

export default function Admin() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const { user, checked, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [topic, setTopic] = useState("");
  const [tag, setTag] = useState("Tecnologia");
  const [generating, setGenerating] = useState(false);
  const [emailType, setEmailType] = useState("welcome");
  const [emailLang, setEmailLang] = useState("ca");
  const [emailReload, setEmailReload] = useState(0);
  const [editingPost, setEditingPost] = useState(null); // null | 'new' | postObject
  const previewUrl = `${BACKEND_URL}/api/admin/email-preview?type=${emailType}&lang=${emailLang}&r=${emailReload}`;

  useEffect(() => {
    if (checked && (!user || user.role !== "admin")) navigate(`/${lang}/login`);
  }, [checked, user, lang, navigate]);

  const loadAll = () => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/blog-admin").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  };

  useEffect(() => { if (user && user.role === "admin") loadAll(); }, [user]);

  const doLogout = async () => { await logout(); navigate(`/${lang}/login`); };

  const generate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setGenerating(true);
    try {
      await api.post("/blog/generate", { topic, tag });
      toast.success("Article generat correctament");
      setTopic("");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error generant l'article");
    } finally { setGenerating(false); }
  };

  const deletePost = async (slug) => {
    if (!window.confirm("Segur que vols eliminar aquest article? Aquesta acció no es pot desfer.")) return;
    try { await api.delete(`/blog/${slug}`); toast.success("Article eliminat"); loadAll(); }
    catch { toast.error("Error"); }
  };

  const togglePublished = async (post) => {
    try {
      await api.patch(`/blog/${post.id}`, { published: !post.published });
      toast.success(post.published ? "Article despublicat" : "Article publicat");
      loadAll();
    } catch { toast.error("Error"); }
  };

  if (!checked) return <div className="min-h-screen flex items-center justify-center text-white/50">Carregant...</div>;
  if (!user || user.role !== "admin") return null;

  const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-neon/60";

  return (
    <div className="min-h-screen bg-black pt-6">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between py-6 border-b border-white/10">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="font-mono text-xs uppercase tracking-widest text-white/40">{"Panell d'administració"}</span>
          </div>
          <button data-testid="admin-logout" onClick={doLogout}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:text-white hover:border-red-400/50 transition-colors">
            <LogOut className="w-4 h-4" /> Sortir
          </button>
        </div>

        <Tabs defaultValue="leads" className="py-8">
          <TabsList className="bg-[#0A0A0A] border border-white/10">
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Contactes ({leads.length})</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog ({posts.length})</TabsTrigger>
            <TabsTrigger value="projects" data-testid="admin-tab-projects">Projectes ({projects.length})</TabsTrigger>
            <TabsTrigger value="emails" data-testid="admin-tab-emails">Correus</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            {leads.length === 0 ? <p className="text-white/40">Encara no hi ha contactes.</p> : (
              <div className="space-y-3">
                {leads.map((l) => (
                  <div key={l.id} data-testid={`admin-lead-${l.id}`} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="font-display font-semibold text-white">{l.name} <span className="text-white/40 font-sans text-sm font-normal">· {l.company || "—"}</span></h3>
                        <p className="text-sm text-neon mt-0.5 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {l.email} {l.phone && `· ${l.phone}`}</p>
                      </div>
                      <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/50">{l.service || "General"}</span>
                    </div>
                    <p className="mt-3 text-white/60 text-sm">{l.message}</p>
                    <p className="mt-2 text-white/30 text-xs">{new Date(l.created_at).toLocaleString("ca-ES")}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="font-display font-semibold text-white text-xl">Articles del blog</h2>
                <p className="text-white/40 text-sm mt-0.5">{posts.length} article{posts.length !== 1 ? "s" : ""} — {posts.filter(p => p.published).length} publicat{posts.filter(p => p.published).length !== 1 ? "s" : ""}.</p>
              </div>
              <button data-testid="admin-new-post-btn" onClick={() => setEditingPost("new")}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white hover:border-neon/50 hover:text-neon transition-colors">
                <Plus className="w-4 h-4" /> Nou article manual
              </button>
            </div>

            <form onSubmit={generate} data-testid="admin-generate-form" className="rounded-2xl border border-neon/25 bg-neon/[0.04] p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-neon" />
                <h3 className="font-display font-semibold text-white">Genera un article amb IA</h3>
              </div>
              <div className="grid sm:grid-cols-[1fr_200px] gap-3">
                <input data-testid="admin-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Tema de l'article (ex: Beneficis de l'IA al comerç local)" className={inputCls} />
                <input data-testid="admin-tag" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Etiqueta" className={inputCls} />
              </div>
              <button data-testid="admin-generate-btn" type="submit" disabled={generating}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60">
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generant (CA/ES/EN/FR)...</> : <><Sparkles className="w-4 h-4" /> Generar article multiidioma</>}
              </button>
            </form>

            <div className="space-y-3">
              {posts.length === 0 && (
                <p className="text-white/40 text-center py-10">Encara no hi ha articles. Crea&apos;n un manualment o genera&apos;n un amb IA.</p>
              )}
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-post-${p.id}`}
                     className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 transition-colors ${p.published ? "border-white/10 bg-[#0A0A0A]" : "border-white/5 bg-[#0A0A0A]/50"}`}>
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {p.cover && <img src={p.cover} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                    <div className="min-w-0">
                      <h3 className={`font-display font-semibold truncate ${p.published ? "text-white" : "text-white/50"}`}>{p.title?.ca || p.title || p.id}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-white/40">{p.tag}</span>
                        {p.published ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-neon/10 border border-neon/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-neon"><Eye className="w-3 h-3" /> Publicat</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white/50"><EyeOff className="w-3 h-3" /> Esborrany</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`admin-toggle-post-${p.id}`} onClick={() => togglePublished(p)}
                            title={p.published ? "Despublicar" : "Publicar"}
                            className="p-2 text-white/40 hover:text-neon transition-colors">
                      {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button data-testid={`admin-edit-post-${p.id}`} onClick={() => setEditingPost(p)}
                            title="Editar"
                            className="p-2 text-white/40 hover:text-neon transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`admin-delete-post-${p.id}`} onClick={() => deletePost(p.id)}
                            title="Eliminar"
                            className="p-2 text-white/40 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <div key={p.id} className="rounded-xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
                  <img src={p.cover} alt={p.name} className="w-full h-32 object-cover" />
                  <div className="p-4">
                    <h3 className="font-display font-semibold text-white">{p.name}</h3>
                    <p className="text-xs text-white/40 mt-1">{p.category?.ca} · {p.year}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 mb-4">
              <div className="flex items-center gap-2 mb-4">
                <MailCheck className="w-5 h-5 text-neon" />
                <h3 className="font-display font-semibold text-white">Vista prèvia dels correus</h3>
                <span className="ml-auto font-mono text-xs uppercase tracking-widest text-white/40">
                  Renderitzat real
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div className="flex flex-col gap-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Tipus</span>
                  <div className="inline-flex rounded-full border border-white/10 bg-black p-1">
                    {EMAIL_TYPES.map((et) => (
                      <button
                        key={et.key}
                        data-testid={`email-type-${et.key}`}
                        onClick={() => setEmailType(et.key)}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                          emailType === et.key
                            ? "bg-neon text-black"
                            : "text-white/60 hover:text-white"
                        }`}
                      >
                        {et.label}
                      </button>
                    ))}
                  </div>
                </div>

                {emailType === "welcome" && (
                  <div className="flex flex-col gap-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Idioma</span>
                    <div className="inline-flex rounded-full border border-white/10 bg-black p-1">
                      {EMAIL_LANGS.map((el) => (
                        <button
                          key={el.key}
                          data-testid={`email-lang-${el.key}`}
                          onClick={() => setEmailLang(el.key)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            emailLang === el.key
                              ? "bg-neon text-black"
                              : "text-white/60 hover:text-white"
                          }`}
                        >
                          {el.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  data-testid="email-reload"
                  onClick={() => setEmailReload((r) => r + 1)}
                  className="ml-auto inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/70 hover:text-white hover:border-neon/50 transition-colors"
                >
                  <Loader2 className="w-3.5 h-3.5" /> Recarregar
                </button>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#111]">
              <iframe
                key={`${emailType}-${emailLang}-${emailReload}`}
                data-testid="email-preview-iframe"
                title="Email preview"
                src={previewUrl}
                className="w-full h-[880px] bg-[#111]"
                sandbox="allow-same-origin"
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {editingPost && (
        <BlogEditor
          post={editingPost === "new" ? null : editingPost}
          onClose={() => setEditingPost(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}
